import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Checkbox, Button, Divider, theme } from 'components';
import styled from 'styled-components';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { InvoiceStatus } from '../../types';

const StatusFilterDropdown: React.FC<FilterDropdownProps> = ({
  confirm,
  setSelectedKeys,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<InvoiceStatus>();
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  const selectedStatusRef = useRef<InvoiceStatus>();

  useEffect(() => {
    selectedStatusRef.current = selectedStatus;
  }, [selectedStatus]);

  const onSubmit = useCallback(() => {
    setSelectedKeys([selectedStatus].filter(Boolean) as React.Key[]);
    confirm();
  }, [selectedStatus, confirm, setSelectedKeys]);

  /*
     When the user clicks outside of the dialog, this handler just acts 
     like the OK button was pushed instead of cancel action.
   */
  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(e.target as Node)
      ) {
        setSelectedKeys(
          [selectedStatusRef.current].filter(Boolean) as React.Key[],
        );
        confirm();
      }
    },
    [confirm, setSelectedKeys],
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [handleClickOutside]);

  const onCheckboxChange = (e: CheckboxChangeEvent) => {
    setSelectedStatus(e.target.checked ? e.target.value : undefined);
  };

  const onReset = () => {
    setSelectedStatus(undefined);
  };

  return (
    <StyledFilter ref={filterDropdownRef}>
      <StyledContainer>
        <StyledCheckboxContainer>
          <div>
            <Checkbox
              value={InvoiceStatus.Paid}
              checked={selectedStatus === InvoiceStatus.Paid}
              onChange={onCheckboxChange}
            >
              Paid
            </Checkbox>
          </div>
          <div>
            <Checkbox
              value={InvoiceStatus.Unpaid}
              checked={selectedStatus === InvoiceStatus.Unpaid}
              onChange={onCheckboxChange}
            >
              Unpaid
            </Checkbox>
          </div>
        </StyledCheckboxContainer>
        <StyledDivider />
        <StyledButtonContainer>
          <StyledResetButton
            type="text"
            disabled={!selectedStatus}
            onClick={onReset}
          >
            Reset
          </StyledResetButton>
          <Button type="primary" onClick={onSubmit}>
            Ok
          </Button>
        </StyledButtonContainer>
      </StyledContainer>
    </StyledFilter>
  );
};

export default StatusFilterDropdown;

const StyledFilter = styled.div`
  min-width: 120px;
  max-width: 200px;
  width: fit-content;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const StyledCheckboxContainer = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledResetButton = styled(Button)`
  color: ${theme.blue5};
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  padding: 10px;
`;
